import React, { useEffect, useState, useCallback, useMemo } from 'react'; // ← ajout useMemo
import { Button, Img, Input, Text } from 'components/componentsPresfor';
import secureStorage from 'hooks/secureStorage';
import ManySignature from 'components/componentsPresfor/ManySignature';
import {
  dtvAttestationValidbyenquete,
  postDtvAttestationValidationEnquete,
  putDtvAttestationValidationEnquete,
} from 'service/api-presfor';
import { SyncLoader } from 'react-spinners';
import { loaderStyles } from 'constantes/global';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import * as yup from 'yup';
import ImageChecker from 'utils/imageCkeck';
import ParticipantsTableau from './participantsTableau';

const validationSchema = yup.object().shape({
  numero_enquete: yup.string().required("Le numéro d'enquête est requis"),
  signature_sous_prefet: yup.string().required('La signature est requise'),
  nom_prenom_sous_prefet: yup.string().required('Le nom et prénom sont requis'),
  date_validation: yup.date().required('La date de validation est requise'),
  date_attestation: yup.date().required('La date de attestation est requise'),
  numero_ordre: yup.string().required("Le numéro d'ordre est requis"),
});

const participantSchema = yup.object().shape({
  nom_prenom: yup.string().required('Le nom et prénom sont requis'),
  qualite: yup.string().required('La qualité est requise'),
  lieu_residence: yup.string().required('Le lieu de résidence est requis'),
  signature: yup.string().required('La signature est requise').nullable(),
});

const ITEMS_PER_PAGE = 10;

const Pagination = ({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
  loading,
}) => {
  if (totalPages <= 1) return null;

  const maxVisible = 5;
  let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
  let end = Math.min(totalPages, start + maxVisible - 1);
  if (end - start + 1 < maxVisible) start = Math.max(1, end - maxVisible + 1);
  const pages = Array.from({ length: end - start + 1 }, (_, i) => start + i);

  const base = 'px-3 py-1 rounded-md text-sm font-medium transition-colors';
  const active = 'bg-cyan-800 text-white';
  const inactive = 'bg-gray-100 text-gray-700 hover:bg-gray-200';
  const dis = 'bg-gray-100 text-gray-400 cursor-not-allowed';

  const from = totalItems > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0;
  const to = Math.min(currentPage * itemsPerPage, totalItems);

  return (
    <div className="flex items-center justify-between mt-4 px-4 py-3 bg-white border-t border-gray-200">
      <p className="text-sm text-gray-700">
        Affichage de <span className="font-medium">{from}</span> à{' '}
        <span className="font-medium">{to}</span> sur{' '}
        <span className="font-medium">{totalItems}</span> résultats
      </p>
      <div className="flex items-center gap-1">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1 || loading}
          className={`${base} flex items-center gap-1 ${currentPage === 1 || loading ? dis : inactive}`}
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          Précédent
        </button>

        {start > 1 && (
          <>
            <button
              onClick={() => onPageChange(1)}
              className={`${base} ${inactive}`}
            >
              1
            </button>
            {start > 2 && <span className="px-2 text-gray-500">…</span>}
          </>
        )}

        {pages.map((p) => (
          <button
            key={p}
            onClick={() => onPageChange(p)}
            disabled={loading}
            className={`${base} ${currentPage === p ? active : inactive}`}
          >
            {p}
          </button>
        ))}

        {end < totalPages && (
          <>
            {end < totalPages - 1 && (
              <span className="px-2 text-gray-500">…</span>
            )}
            <button
              onClick={() => onPageChange(totalPages)}
              className={`${base} ${inactive}`}
            >
              {totalPages}
            </button>
          </>
        )}

        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages || loading}
          className={`${base} flex items-center gap-1 ${currentPage === totalPages || loading ? dis : inactive}`}
        >
          Suivant
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5l7 7-7 7"
            />
          </svg>
        </button>
      </div>
    </div>
  );
};

export const AttestationValidationEnqueteFonciere = ({
  setActiveTab,
  setReq,
}) => {
  const enquete = secureStorage.getItem('enquete_courante');
  const enqueteObj = JSON.parse(enquete);
  const navigate = useNavigate();

  const [state, setState] = useState({
    signature: '',
    nomPrenomSousPrefet: '',
    date: null,
    dateAttestation: new Date(),
    numero_ordre: '',
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState(null);
  const [existID, setExistID] = useState(null);
  const [toggle, setToggle] = useState(false);

  const updateState = (key, value) => {
    setState((prev) => ({ ...prev, [key]: value }));
    setErrors(null);
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await dtvAttestationValidbyenquete(
          enqueteObj?.numero_enquete
        );
        if (res?.data?.data?.dtv_attestation_validation) {
          const data = res.data.data.dtv_attestation_validation;
          setState({
            signature: data.signature_sous_prefet,
            nomPrenomSousPrefet: data.nom_prenom_sous_prefet,
            date: data.date_validation,
            dateAttestation: data.date_attestation
              ? new Date(data.date_attestation)
              : new Date(),
            numero_ordre: data.numero_ordre || '',
          });
          setExistID(data.id);
        }
      } catch (error) {
        console.error(error);
        showError("Une erreur s'est produite lors du chargement de l'enquête");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [enqueteObj?.numero_enquete]);

  const showError = (message) => {
    Swal.fire({
      text: message,
      showConfirmButton: false,
      heightAuto: false,
      timer: 2000,
      customClass: {
        popup: 'h-1/2 flex items-center justify-center text-lg',
      },
    });
  };

  const handleApiCall = async (apiCall, successMessage) => {
    setLoading(true);
    try {
      const res = await apiCall;
      if (res?.data?.code === 200) {
        showError(successMessage || res.data.message);
        navigate('/presfor/psfcpresforlivrablesdelimitation');
      } else {
        showError(res?.data?.message || "Une erreur s'est produite");
      }
    } catch (err) {
      console.error(err);
      showError("Une erreur s'est produite");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    const req = {
      numero_enquete: enqueteObj?.numero_enquete,
      signature_sous_prefet: state.signature,
      nom_prenom_sous_prefet: state.nomPrenomSousPrefet,
      date_validation: state.date,
      date_attestation: state.dateAttestation,
      numero_ordre: state.numero_ordre,
    };

    try {
      await validationSchema.validate(req, { abortEarly: false });
      setActiveTab('annexe');
      setReq(req);
    } catch (err) {
      const errorsTemp = err.inner.reduce((acc, error) => {
        acc[error.path] = error.message;
        return acc;
      }, {});
      setErrors(errorsTemp);
    }
  };

  const annuller = () => {
    navigate('/presfor/psfcpresforlivrablesdelimitation');
  };

  return (
    <>
      <div style={loaderStyles}>
        <SyncLoader color="#4890a6" loading={loading} size={15} />
      </div>

      <div className="flex flex-col items-center justify-start w-full">
        <div className="flex flex-col items-center justify-start w-full">
          <div className="bg-white-A700 flex flex-col items-center justify-center sm:px-5 px-8 py-5 rounded w-full">
            <div className="bg-white-A700 flex flex-col gap-2.5 items-center justify-start p-2.5 w-full">
              <div className="flex flex-col gap-[15px] items-center justify-start w-full">
                <div className="flex flex-col items-center justify-center w-full">
                  <div className="flex flex-col items-center justify-start w-full">
                    <Img
                      className="h-[85px] md:h-auto object-cover w-[89px] sm:w-full"
                      src="/images/img_coatofarmsof.png"
                      alt="logoaforOne"
                    />
                    <div className="flex flex-col items-center justify-start w-full">
                      <Text className="text-black-900 text-center text-inter text-xs w-full">
                        REPUBLIQUE DE COTE D'IVOIRE
                        <br />
                        Union – Discipline – Travail
                        <br />
                        -------------
                        <br />
                        <b>
                          MINISTERE DE L'AGRICULTURE ET DU DEVELOPPEMENT RURAL
                        </b>
                        <br />
                      </Text>
                    </div>

                    <ul className="flex flex-col w-full gap-2 text-xs mt-6">
                      <li>
                        1. Département de <b>{enqueteObj?.departement}</b>
                      </li>
                      <li>
                        2. CSPGFR de la Sous-Préfecture de{' '}
                        <b>{enqueteObj.sous_prefecture}</b>
                      </li>
                      <li>
                        3. CVGFR du village de <b>{enqueteObj.village}</b>
                      </li>
                    </ul>

                    <div className="font-medium text-base leading-6 mt-6 text-center">
                      <p>ATTESTATION DE VALIDATION D'UNE ENQUETE</p>
                      <p>FONCIERE RURALE OFFICIELLE</p>
                    </div>

                    <div className="flex flex-row items-center justify-between mt-6 w-full">
                      <div className="flex items-start">
                        <p className="text-xs">
                          N°
                          <Input
                            type="text"
                            value={state.numero_ordre}
                            onChange={(e) => updateState('numero_ordre', e)}
                            className="!placeholder:text-black-900_b2 text-black-900 font-inter leading-[normal] md:h-auto p-0 sm:h-auto text-left text-xs w-full"
                            wrapClassName="border border-gray-300 border-solid rounded-md w-full"
                            shape="round"
                            color="white_A700"
                            size="lg"
                            variant="fill"
                          />
                        </p>
                      </div>
                      <div className="flex items-start">
                        <p className="text-xs">
                          du{' '}
                          <Input
                            type="date"
                            value={
                              state.dateAttestation
                                ? state.dateAttestation
                                    .toISOString()
                                    .split('T')[0]
                                : ''
                            }
                            onChange={(e) =>
                              updateState('dateAttestation', new Date(e))
                            }
                            className="!placeholder:text-black-900_b2 text-black-900 font-inter leading-[normal] md:h-auto p-0 sm:h-auto text-left text-xs w-full"
                            wrapClassName="border border-gray-300 border-solid rounded-md w-full"
                            shape="round"
                            color="white_A700"
                            size="lg"
                            variant="fill"
                          />{' '}
                        </p>
                      </div>
                    </div>
                    <div className="text-red-500">
                      {errors?.numero_ordre || errors?.date_attestation}
                    </div>

                    <div className="mt-8 text-xs leading-6 text-left w-full">
                      <div>
                        <p className="mb-4">
                          Le Sous-Préfet de{' '}
                          <span className="font-bold">
                            {enqueteObj.sous_prefecture}
                          </span>
                        </p>

                        <p className="mb-4">
                          Président du Comité Sous-préfectoral de Gestion
                          Foncière Rurale,
                        </p>

                        <p className="mb-2">
                          atteste que l'enquête officielle relative à la
                          délimitation du territoire du village
                        </p>

                        <p className="mb-2">
                          de{' '}
                          <span className="font-bold underline">
                            {enqueteObj.village}
                          </span>
                        </p>

                        <p className="mb-4">
                          a été validée par le Comité sous-préfectoral de
                          Gestion Foncière Rural
                        </p>

                        <p className="mb-4">
                          de{' '}
                          <span className="font-bold underline">
                            {enqueteObj.sous_prefecture}
                          </span>
                        </p>
                      </div>

                      <div className="flex flex-col gap-2">
                        <p className="text-justify">
                          au cours de sa séance de travail du{' '}
                          <input
                            type="date"
                            className="border border-gray-300 rounded-md p-1"
                            value={
                              state.date
                                ? new Date(state.date)
                                    .toISOString()
                                    .split('T')[0]
                                : ''
                            }
                            onChange={(e) =>
                              updateState('date', new Date(e.target.value))
                            }
                          />
                          <div className="text-red-500">
                            {errors?.date_validation}
                          </div>
                          ,
                        </p>
                      </div>

                      {/* Signature Section */}
                      <div className="flex justify-end w-full mt-12">
                        <div className="text-xs text-center">
                          <p className="mt-4 font-semibold">
                            Le Sous-Préfet de {enqueteObj.sous_prefecture}
                          </p>
                          <div className="flex gap-4 mt-4">
                            <Input
                              placeholder="Nom et Prénom du Sous-Préfet"
                              value={state.nomPrenomSousPrefet}
                              onChange={(e) =>
                                updateState('nomPrenomSousPrefet', e)
                              }
                              className="!placeholder:text-black-900_b2 text-black-900 font-inter leading-[normal] md:h-auto p-0 sm:h-auto text-left text-xs w-full"
                              wrapClassName="border border-gray-300 border-solid rounded-md w-full"
                              shape="round"
                              color="white_A700"
                              size="lg"
                              variant="fill"
                            />
                          </div>
                          <div className="text-red-500">
                            {errors?.nom_prenom_sous_prefet}
                          </div>
                          <div className="flex flex-col items-center justify-center mt-4">
                            <>
                              {toggle || !state.signature ? (
                                <>
                                  <ManySignature
                                    onSignatureChange={(sig) =>
                                      updateState('signature', sig)
                                    }
                                  />
                                </>
                              ) : (
                                <>
                                  <ImageChecker
                                    src={state.signature}
                                    alt={'oldSignature'}
                                    fallbackMessage={
                                      'Signature vide ou non valide'
                                    }
                                  />
                                  <button
                                    className="signature-button-delete"
                                    onClick={() => setToggle(true)}
                                  >
                                    Reprendre la signature{' '}
                                  </button>
                                </>
                              )}
                              <div className="text-red-500">
                                {errors?.signature_sous_prefet}
                              </div>
                            </>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-center w-full">
                    <Button
                      className="cursor-pointer font-inter font-medium h-10 text-center text-xs w-[154px] mt-4 mr-4"
                      shape="round"
                      color="cyan_900"
                      size="sm"
                      variant="fill"
                      onClick={annuller}
                    >
                      Annuler
                    </Button>
                    <Button
                      className="cursor-pointer font-inter font-medium h-10 text-center text-xs w-[154px] mt-4"
                      shape="round"
                      color="cyan_900"
                      size="md"
                      variant="fill"
                      onClick={handleSubmit}
                    >
                      Suivant
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export const AnnexeAttestationValidation = ({ setActiveTab, createData }) => {
  const enquete = secureStorage.getItem('enquete_courante');
  const enqueteObj = JSON.parse(enquete);
  const navigate = useNavigate();

  const [state, setState] = useState({
    loading: false,
    errors: null,
    dtv_attestation_validation: null,
    parties: [],
    participantToUpdate: null,
    isEdit: false,
    index: null,
    validLps: [],
    isExist: false,
    status: '',
    invalidationReasons: '',
  });

  // Pagination //
  const [currentPage, setCurrentPage] = useState(1);

  const totalItems = state.validLps.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / ITEMS_PER_PAGE));
  const safePage = Math.min(currentPage, totalPages);

  const paginatedValidLps = useMemo(() => {
    const start = (safePage - 1) * ITEMS_PER_PAGE;
    return state.validLps.slice(start, start + ITEMS_PER_PAGE);
  }, [state.validLps, safePage]);

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const fetchData = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true }));
    try {
      const res = await dtvAttestationValidbyenquete(
        enqueteObj?.numero_enquete
      );
      if (res?.data?.data?.dtv_attestation_validation) {
        setState((prev) => ({
          ...prev,
          isExist: true,
          dtv_attestation_validation: res.data.data.dtv_attestation_validation,
          validLps: res.data.data.dtv_attestation_validation?.validLps || [],
          status: res.data.data.dtv_attestation_validation?.status || '',
          invalidationReasons:
            res.data.data.dtv_attestation_validation?.invalidationReasons || '',
        }));
      }
    } catch (error) {
      console.error(error);
      showError("Une erreur s'est produite lors du chargement de l'enquête");
    } finally {
      setState((prev) => ({ ...prev, loading: false }));
    }
  }, [enqueteObj?.numero_enquete]);

  useEffect(() => {
    fetchData();
  }, [fetchData, createData]);

  const showError = (message) => {
    Swal.fire({
      text: message,
      showConfirmButton: false,
      heightAuto: false,
      timer: 2000,
      customClass: {
        popup: 'h-1/2 flex items-center justify-center text-lg',
      },
    });
  };

  const handleApiCall = async (apiCall, successMessage) => {
    setState((prev) => ({ ...prev, loading: true }));
    try {
      const res = await apiCall;
      if (res?.data?.code === '200' || res?.data?.code === '201') {
        showError(successMessage || res.data.message);
        navigate('/presfor/psfcpresforlivrablesdelimitation');
      } else {
        showError(res?.data?.message || "Une erreur s'est produite");
      }
    } catch (err) {
      console.error(err);
      showError("Une erreur s'est produite");
    } finally {
      setState((prev) => ({ ...prev, loading: false }));
    }
  };

  const handleSubmit = async () => {
    try {
      const {
        isExist,
        dtv_attestation_validation,
        parties,
        validLps,
        status,
        invalidationReasons,
      } = state;

      const baseData = {
        ...createData,
        parties: validLps.map((p, i) => ({ ...p, nbr_order: i })),
        status: status,
        invalidationReasons: invalidationReasons,
      };

      if (isExist) {
        const req = {
          ...baseData,
          parties: [...parties, ...baseData.parties],
          numero_enquete: enqueteObj?.numero_enquete,
        };
        await handleApiCall(
          putDtvAttestationValidationEnquete(dtv_attestation_validation?.id, {
            data: req,
          }),
          'Attestation mise à jour avec succès'
        );
      } else {
        await handleApiCall(
          postDtvAttestationValidationEnquete({ data: baseData }),
          'Attestation créée avec succès'
        );
      }
    } catch (error) {
      console.error(error);
      showError("Une erreur s'est produite lors de l'enregistrement");
    }
  };

  const onAddParticipant = (participant) => {
    setState((prev) => {
      const newParticipant = {
        ...participant,
        nbr_order: prev.validLps.length,
      };
      const newValidLps = [...prev.validLps, newParticipant].sort(
        (a, b) => a.nbr_order - b.nbr_order
      );
      // Aller à la dernière page pour voir le nouveau participant
      setCurrentPage(Math.ceil(newValidLps.length / ITEMS_PER_PAGE));
      return {
        ...prev,
        validLps: newValidLps,
        parties: [...prev.parties, { action: 'add', ...newParticipant }],
      };
    });
  };

  const onRemoveParticipant = (indexInPage) => {
    // Convertir l'index de page en index réel dans validLps
    const realIndex = (safePage - 1) * ITEMS_PER_PAGE + indexInPage;
    setState((prev) => {
      const participant = prev.validLps[realIndex];
      const updatedValidLps = prev.validLps.filter((_, i) => i !== realIndex);

      const newParties = participant?.id
        ? [...prev.parties, { action: 'delete', ...participant }]
        : prev.parties.filter(
            (p) =>
              !(
                p.action === 'add' &&
                p.nom_prenom === participant.nom_prenom &&
                p.qualite === participant.qualite &&
                p.lieu_residence === participant.lieu_residence
              )
          );

      const newValidLps = updatedValidLps.map((p, i) => ({
        ...p,
        nbr_order: i,
      }));
      // Reculer d'une page si la page courante devient vide
      const newTotalPages = Math.max(
        1,
        Math.ceil(newValidLps.length / ITEMS_PER_PAGE)
      );
      if (currentPage > newTotalPages) setCurrentPage(newTotalPages);

      return {
        ...prev,
        validLps: newValidLps,
        parties: newParties,
      };
    });
  };

  const onUpdateParticipant = (indexInPage, participant) => {
    // Convertir l'index de page en index réel dans validLps
    const realIndex = (safePage - 1) * ITEMS_PER_PAGE + indexInPage;
    setState((prev) => {
      const oldParticipant = prev.validLps[realIndex];
      const updatedValidLps = [...prev.validLps];

      updatedValidLps[realIndex] = {
        ...participant,
        nbr_order: oldParticipant?.nbr_order ?? realIndex,
      };

      const newParties = oldParticipant?.id
        ? [
            ...prev.parties,
            {
              action: 'update',
              ...participant,
              id: oldParticipant.id,
            },
          ]
        : prev.parties;

      return {
        ...prev,
        validLps: updatedValidLps.sort((a, b) => a.nbr_order - b.nbr_order),
        parties: newParties,
        isEdit: false,
      };
    });
  };

  const handleModifyRow = (indexInPage) => {
    // Convertir l'index de page en index réel dans validLps
    const realIndex = (safePage - 1) * ITEMS_PER_PAGE + indexInPage;
    setState((prev) => ({
      ...prev,
      isEdit: true,
      index: indexInPage, // on garde l'index de page pour le passer au formulaire
      participantToUpdate: prev.validLps[realIndex],
    }));
  };

  const annuller = () => {
    setActiveTab('attestation');
  };

  const handleStatusChange = (e) => {
    setState((prev) => ({ ...prev, status: e }));
  };

  const handleInvalidationReasonsChange = (e) => {
    setState((prev) => ({ ...prev, invalidationReasons: e.target.value }));
  };

  return (
    <>
      <div style={loaderStyles}>
        <SyncLoader color="#4890a6" loading={state.loading} size={15} />
      </div>

      <div className="bg-white-A700 flex flex-col items-center justify-center sm:px-5 px-8 py-5 rounded w-full">
        <div className="flex flex-col items-center justify-start w-[1100px]">
          <div className="text-center text-sm mb-4">
            <Text className="font-medium">Annexe à l'attestation</Text>
          </div>

          <div className="text-xs mb-6">
            <Text>
              Liste des participants à la séance de validation des résultats de
              l'enquête officielle relative à la délimitation du territoire
              villageois de{' '}
              <span className="font-bold">{enqueteObj?.village}</span>, S/P de{' '}
              <span className="font-bold">{enqueteObj?.sous_prefecture}</span>
            </Text>
          </div>

          <div className="flex items-start w-full">
            <div className="w-1/3">
              {state.isEdit ? (
                <UpdateParticipantForm
                  index={state.index}
                  participant={state.participantToUpdate}
                  onUpdateParticipant={onUpdateParticipant}
                  setIsEdit={(value) =>
                    setState((prev) => ({ ...prev, isEdit: value }))
                  }
                />
              ) : (
                <AddParticipantForm onAddParticipant={onAddParticipant} />
              )}
            </div>
            <div className="w-2/3">
              <ParticipantsTableau
                participants={paginatedValidLps}
                handleRemoveRow={onRemoveParticipant}
                handleModifyRow={handleModifyRow}
                status={state.status}
                invalidationReasons={state.invalidationReasons}
                onStatusChange={handleStatusChange}
                onInvalidationReasonsChange={handleInvalidationReasonsChange}
                startIndex={(safePage - 1) * ITEMS_PER_PAGE}
              />

              {/* Pagination */}
              <Pagination
                currentPage={safePage}
                totalPages={totalPages}
                totalItems={totalItems}
                itemsPerPage={ITEMS_PER_PAGE}
                onPageChange={handlePageChange}
                loading={state.loading}
              />
            </div>
          </div>

          <div className="flex items-center justify-center w-full">
            <Button
              className="cursor-pointer font-inter font-medium h-10 text-center text-xs w-[154px] mt-4 mr-4"
              shape="round"
              color="cyan_900"
              size="sm"
              variant="fill"
              onClick={annuller}
            >
              Retour
            </Button>

            <Button
              className="cursor-pointer font-inter font-medium h-10 text-center text-xs w-[154px] mt-4"
              shape="round"
              color="cyan_900"
              size="sm"
              variant="fill"
              onClick={handleSubmit}
            >
              {state.dtv_attestation_validation ? 'Modifier' : 'Enregistrer'}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

const AddParticipantForm = ({ onAddParticipant }) => {
  const [state, setState] = useState({
    nom: '',
    qualite: '',
    residence: '',
    signature: null,
    status: '',
    invalidationReasons: '',
    date: new Date(),
  });

  const [errors, setErrors] = useState(null);
  const [toggle, setToggle] = useState(false);

  const updateState = (key, value) => {
    setState((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async () => {
    const participant = {
      nom_prenom: state.nom,
      qualite: state.qualite,
      lieu_residence: state.residence,
      signature: state.signature,
      date_naissance: state.date,
      status: state.status,
      invalidationReasons: state.invalidationReasons,
    };

    try {
      await participantSchema.validate(participant, { abortEarly: false });
      setErrors(null);
      onAddParticipant(participant);
      setState({
        nom: '',
        qualite: '',
        residence: '',
        signature: null,
        date: new Date(),
        status: '',
        invalidationReasons: '',
      });
    } catch (err) {
      const errorsTemp = err.inner.reduce((acc, error) => {
        acc[error.path] = error.message;
        return acc;
      }, {});
      setErrors(errorsTemp);
    }
  };

  const annuller = () => {
    setState({
      nom: '',
      qualite: '',
      residence: '',
      signature: null,
      date: new Date(),
      status: '',
      invalidationReasons: '',
    });
  };

  return (
    <>
      <div className="flex flex-col gap-[10px] items-center justify-start w-full">
        <div className="flex md:flex-col flex-row gap-[100px] items-start justify-center w-auto md:w-full">
          <div className="flex flex-col gap-[5px] items-start justify-start w-[270px] sm:w-full">
            <div className="flex flex-col gap-[5px] items-start justify-center w-full">
              <div className="flex flex-col items-start justify-center w-full">
                <Text className="w-auto text-xs text-black-900">
                  Nom et Prénoms
                </Text>
                <Input
                  placeholder="Nom et prénoms"
                  value={state.nom}
                  onChange={(e) => updateState('nom', e)}
                  className="!placeholder:text-black-900_b2 text-black-900 font-inter leading-[normal] md:h-auto p-0 sm:h-auto text-left text-xs w-full"
                  wrapClassName="border border-gray-300 border-solid rounded-md w-full"
                  shape="round"
                  color="white_A700"
                  size="lg"
                  variant="fill"
                />
                <div className="text-red-500">{errors?.nom_prenom}</div>
              </div>

              <div className="flex flex-col items-start justify-center w-full">
                <Text className="w-auto text-xs text-black-900">Qualité</Text>
                <Input
                  placeholder="Qualité"
                  value={state.qualite}
                  onChange={(e) => updateState('qualite', e)}
                  className="!placeholder:text-black-900_b2 text-black-900 font-inter leading-[normal] md:h-auto p-0 sm:h-auto text-left text-xs w-full"
                  wrapClassName="border border-gray-300 border-solid rounded-md w-full"
                  shape="round"
                  color="white_A700"
                  size="lg"
                  variant="fill"
                />
                <div className="text-red-500">{errors?.qualite}</div>
              </div>

              <div className="flex flex-col items-start justify-center w-full">
                <Text className="w-auto text-xs text-black-900">
                  Lieu de résidence
                </Text>
                <Input
                  placeholder="Lieu de résidence"
                  value={state.residence}
                  onChange={(e) => updateState('residence', e)}
                  className="!placeholder:text-black-900_b2 text-black-900 font-inter leading-[normal] md:h-auto p-0 sm:h-auto text-left text-xs w-full"
                  wrapClassName="border border-gray-300 border-solid rounded-md w-full"
                  shape="round"
                  color="white_A700"
                  size="lg"
                  variant="fill"
                />
                <div className="text-red-500">{errors?.lieu_residence}</div>
              </div>

              <div className="flex flex-col items-start justify-center w-full">
                <Text className="w-auto text-xs text-black-900">Statut</Text>
                <Input
                  placeholder="Statut"
                  value={state.status}
                  onChange={(e) => updateState('status', e)}
                  className="!placeholder:text-black-900_b2 text-black-900 font-inter leading-[normal] md:h-auto p-0 sm:h-auto text-left text-xs w-full"
                  wrapClassName="border border-gray-300 border-solid rounded-md w-full"
                  shape="round"
                  color="white_A700"
                  size="lg"
                  variant="fill"
                />
              </div>

              <div className="flex flex-col items-start justify-center w-full">
                <Text className="w-auto text-xs text-black-900">
                  Raisons d'invalidation
                </Text>
                <textarea
                  placeholder="Raisons d'invalidation"
                  value={state.invalidationReasons}
                  onChange={(e) =>
                    updateState('invalidationReasons', e.target.value)
                  }
                  className="!placeholder:text-black-900_b2 text-black-900 font-inter leading-[normal] md:h-auto sm:h-auto text-left text-xs w-full rounded-md p-2"
                  rows="3"
                />
              </div>

              <div className="flex flex-col items-start justify-center w-full">
                <Text className="w-auto text-xs text-black-900">Signature</Text>
                <div className="flex flex-col items-center justify-center mt-1">
                  {toggle || !state.signature ? (
                    <ManySignature
                      onSignatureChange={(sig) => updateState('signature', sig)}
                    />
                  ) : (
                    <>
                      <ImageChecker
                        src={state.signature}
                        alt="oldSignature"
                        fallbackMessage="Signature vide ou non valide"
                      />
                      <button
                        className="signature-button-delete"
                        onClick={() => setToggle(true)}
                      >
                        Reprendre la signature
                      </button>
                    </>
                  )}
                  <div className="text-red-500">{errors?.signature}</div>
                </div>
              </div>

              <div className="flex items-center justify-center w-full">
                <button className="button-ajout-membre" onClick={annuller}>
                  Annuler
                </button>
                <button className="button-ajout-membre" onClick={handleSubmit}>
                  Ajouter
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const UpdateParticipantForm = ({
  index,
  participant,
  onUpdateParticipant,
  setIsEdit,
}) => {
  const [state, setState] = useState({
    nom: participant.nom_prenom || '',
    qualite: participant.qualite || '',
    residence: participant.lieu_residence || '',
    signature: participant.signature || null,
    date: participant.date_naissance || new Date(),
    status: participant.status || '',
    invalidationReasons: participant.invalidationReasons || '',
  });

  const [toggle, setToggle] = useState(false);

  const updateState = (key, value) => {
    setState((prev) => ({ ...prev, [key]: value }));
  };

  const annuller = () => {
    setIsEdit(false);
  };

  const handleSubmit = () => {
    const updatedParticipant = {
      nom_prenom: state.nom,
      qualite: state.qualite,
      lieu_residence: state.residence,
      signature: state.signature,
      date_naissance: state.date,
      status: state.status,
      invalidationReasons: state.invalidationReasons,
    };
    onUpdateParticipant(index, updatedParticipant);
  };

  return (
    <>
      <div className="flex flex-col gap-[10px] items-center justify-start w-full">
        <div className="flex md:flex-col flex-row gap-[100px] items-start justify-center w-auto md:w-full">
          <div className="flex flex-col gap-[5px] items-start justify-start w-[270px] sm:w-full">
            <div className="flex flex-col gap-[5px] items-start justify-center w-full">
              <div className="flex flex-col items-start justify-center w-full">
                <Text className="w-auto text-xs text-black-900">
                  Nom et Prénoms
                </Text>
                <Input
                  placeholder="Nom et prénoms"
                  value={state.nom}
                  onChange={(e) => updateState('nom', e)}
                  className="!placeholder:text-black-900_b2 text-black-900 font-inter leading-[normal] md:h-auto p-0 sm:h-auto text-left text-xs w-full"
                  wrapClassName="border border-gray-300 border-solid rounded-md w-full"
                  shape="round"
                  color="white_A700"
                  size="lg"
                  variant="fill"
                />
              </div>

              <div className="flex flex-col items-start justify-center w-full">
                <Text className="w-auto text-xs text-black-900">Qualité</Text>
                <Input
                  placeholder="Qualité"
                  value={state.qualite}
                  onChange={(e) => updateState('qualite', e)}
                  className="!placeholder:text-black-900_b2 text-black-900 font-inter leading-[normal] md:h-auto p-0 sm:h-auto text-left text-xs w-full"
                  wrapClassName="border border-gray-300 border-solid rounded-md w-full"
                  shape="round"
                  color="white_A700"
                  size="lg"
                  variant="fill"
                />
              </div>

              <div className="flex flex-col items-start justify-center w-full">
                <Text className="w-auto text-xs text-black-900">
                  Lieu de résidence
                </Text>
                <Input
                  placeholder="Lieu de résidence"
                  value={state.residence}
                  onChange={(e) => updateState('residence', e)}
                  className="!placeholder:text-black-900_b2 text-black-900 font-inter leading-[normal] md:h-auto p-0 sm:h-auto text-left text-xs w-full"
                  wrapClassName="border border-gray-300 border-solid rounded-md w-full"
                  shape="round"
                  color="white_A700"
                  size="lg"
                  variant="fill"
                />
              </div>

              <div className="flex flex-col items-start justify-center w-full">
                <Text className="w-auto text-xs text-black-900">Statut</Text>
                <Input
                  placeholder="Statut"
                  value={state.status}
                  onChange={(e) => updateState('status', e)}
                  className="!placeholder:text-black-900_b2 text-black-900 font-inter leading-[normal] md:h-auto p-0 sm:h-auto text-left text-xs w-full"
                  wrapClassName="border border-gray-300 border-solid rounded-md w-full"
                  shape="round"
                  color="white_A700"
                  size="lg"
                  variant="fill"
                />
              </div>

              <div className="flex flex-col items-start justify-center w-full">
                <Text className="w-auto text-xs text-black-900">
                  Raisons d'invalidation
                </Text>
                <textarea
                  placeholder="Raisons d'invalidation"
                  value={state.invalidationReasons}
                  onChange={(e) =>
                    updateState('invalidationReasons', e.target.value)
                  }
                  className="!placeholder:text-black-900_b2 text-black-900 font-inter leading-[normal] md:h-auto sm:h-auto text-left text-xs w-full rounded-md p-2"
                  rows="3"
                />
              </div>

              <div className="flex flex-col items-start justify-center w-full">
                <Text className="w-auto text-xs text-black-900">Signature</Text>
                <div className="flex flex-col items-center justify-center mt-1">
                  {toggle || !state.signature ? (
                    <ManySignature
                      onSignatureChange={(sig) => updateState('signature', sig)}
                    />
                  ) : (
                    <>
                      <ImageChecker
                        src={state.signature}
                        alt="oldSignature"
                        fallbackMessage="Signature vide ou non valide"
                      />
                      <button
                        className="signature-button-delete"
                        onClick={() => setToggle(true)}
                      >
                        Reprendre la signature
                      </button>
                    </>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-center w-full">
                <button className="button-ajout-membre" onClick={annuller}>
                  Annuler
                </button>
                <button className="button-ajout-membre" onClick={handleSubmit}>
                  Modifier
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AttestationValidationEnqueteFonciere;
